import { NO, UIRectangle, UIView, YES } from "uicore-ts"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHButtons, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHNewProcurementViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"


export type ProcurementTemplateLockControlConfiguration = {
    locked: boolean
    lockDidChange: (locked: boolean) => void
    isFieldHidden?: boolean
    fieldVisibilityDidChange?: (isFieldHidden: boolean) => void
}


export class ProcurementTemplateLockButton extends RHButton {
    readonly subject: string
    _locked = NO
    lockDidChange?: (locked: boolean) => void
    isFieldHidden = NO
    fieldVisibilityDidChange?: (isFieldHidden: boolean) => void
    readonly visibilityButton = new RHButton().configuredWithObject({
        contentPadding: RHNewProcurementViewTokens.templateLockControl.contentPadding,
        style: {
            padding: RHNewProcurementViewTokens.templateLockControl.padding,
            borderRadius: RHNewProcurementViewTokens.templateLockControl.borderRadius
        }
    })

    constructor(subject: string) {
        super()
        this.subject = subject
        this.contentPadding = RHNewProcurementViewTokens.templateLockControl.contentPadding
        this.configureWithObject({
            // @ts-ignore - Same token-backed icon-button style contract as shared dialog close controls.
            style: {
                padding: RHNewProcurementViewTokens.templateLockControl.padding,
                borderRadius: RHNewProcurementViewTokens.templateLockControl.borderRadius
            }
        })
        this.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.locked = !this.locked
            this.lockDidChange?.(this.locked)
        }
        this.visibilityButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            if (!this.locked || !this.enabled) {
                return
            }
            this.isFieldHidden = !this.isFieldHidden
            this._updateVisibilityButton()
            this.fieldVisibilityDidChange?.(this.isFieldHidden)
        }
        this.locked = NO
    }

    set locked(value: boolean) {
        this._locked = value
        if (!value) {
            this.isFieldHidden = NO
        }
        let iconPath =
            "M15 10V7a3 3 0 0 0-5.83-1H6.05A6 6 0 0 1 18 7v3h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h10Z"
        let title = "Lukustamata"
        let pressed = "false"
        this.variant = "success"
        if (value) {
            iconPath =
                "M6 10V7a6 6 0 0 1 12 0v3h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h1Zm3 0h6V7a3 3 0 0 0-6 0v3Z"
            title = "Lukustatud"
            pressed = "true"
            this.variant = RHButtons.destructiveUtilityVariant
        }
        const iconSize = RHNewProcurementViewTokens.templateLockControl.iconSize
        this.titleLabel.innerHTML =
            `<svg aria-hidden="true" width="${iconSize}" height="${iconSize}" ` +
            `viewBox="0 0 24 24" fill="currentColor"><path d="${iconPath}"/></svg>`
        this.viewHTMLElement.setAttribute("aria-pressed", pressed)
        this.viewHTMLElement.setAttribute("aria-label", `${this.subject}: ${title}`)
        this.viewHTMLElement.setAttribute("title", title)
        this._updateVisibilityButton()
        this.setNeedsLayout()
    }

    get locked(): boolean {
        return this._locked
    }

    layoutBesideTitleLabelInFrame(titleLabel: UIView, rowFrame: UIRectangle) {
        const size = RHNewProcurementViewTokens.templateLockControl.size
        const gap = RHNewProcurementViewTokens.templateLockControl.gap
        rowFrame
            .IF(this.superview === titleLabel.superview)
            .rectangleWithWidth(Math.max(0, Math.min(
                rowFrame.width - this.widthIncludingVisibilityButton - gap,
                titleLabel.intrinsicContentWidth(rowFrame.height) + RHSpacing.medium
            )))
            .assignedAsFrameOfView(titleLabel)
            .rectangleForNextColumn(gap, size)
            .rectangleWithHeight(size, 0.5)
            .performingFunctionWithSelf(frame => this.layoutInFrame(frame))
            .ELSE()
            .assignedAsFrameOfView(titleLabel)
            .ENDIF()
    }


    get widthIncludingVisibilityButton(): number {
        const size = RHNewProcurementViewTokens.templateLockControl.size
        if (this.locked && this.fieldVisibilityDidChange) {
            return size * 2 + RHSpacing.small + RHNewProcurementViewTokens.templateLockControl.gap
        }
        return size
    }


    layoutInFrame(frame: UIRectangle) {
        const size = RHNewProcurementViewTokens.templateLockControl.size
        frame
            .rectangleWithWidth(size)
            .assignedAsFrameOfView(this)
            .IF(!!this.superview && this.visibilityButton.superview === this.superview)
            .rectangleForNextColumn(RHNewProcurementViewTokens.templateLockControl.gap, size + RHSpacing.small)
            .rectangleWithHeight(size + RHSpacing.small, 0.5)
            .assignedAsFrameOfView(this.visibilityButton)
            .ENDIF()
    }


    override didMoveToSuperview(superview: UIView) {
        super.didMoveToSuperview(superview)
        this._updateVisibilityButton()
    }


    override removeFromSuperview() {
        super.removeFromSuperview()
        this.visibilityButton.removeFromSuperview()
    }


    _updateVisibilityButton() {
        if (!this.locked || !this.fieldVisibilityDidChange || !this.superview) {
            this.visibilityButton.removeFromSuperview()
            return
        }
        const iconSize = RHNewProcurementViewTokens.templateLockControl.size
        const iconPath = "M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5Zm0 12a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9Zm0-7.2a2.7 2.7 0 1 0 0 5.4 2.7 2.7 0 0 0 0-5.4Z"
        let iconStrikeThrough = ""
        let title = "Nähtav malli kasutajale — peida väli"
        let pressed = "false"
        this.visibilityButton.variant = "link"
        if (this.isFieldHidden) {
            iconStrikeThrough = '<path d="M3 3 21 21" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'
            title = "Peidetud malli kasutajale — näita välja"
            pressed = "true"
            this.visibilityButton.configureWithObject({
                colors: {
                    ...this.visibilityButton.colors,
                    titleLabel: { ...this.colors.titleLabel }
                }
            })
            this.visibilityButton.updateContentForCurrentState()
        }
        this.visibilityButton.titleLabel.innerHTML =
            `<svg aria-hidden="true" width="${iconSize}" height="${iconSize}" ` +
            `viewBox="0 0 24 24" fill="currentColor"><path d="${iconPath}"/>${iconStrikeThrough}</svg>`
        this.visibilityButton.hoverText = title
        this.visibilityButton.viewHTMLElement.setAttribute("aria-label", `${this.subject}: ${title}`)
        this.visibilityButton.viewHTMLElement.setAttribute("aria-pressed", pressed)
        this.visibilityButton.addedAsSubviewToView(this.superview)
        this.superview.setNeedsLayoutUpToRootView()
    }


    configure(configuration: ProcurementTemplateLockControlConfiguration) {
        this.lockDidChange = configuration.lockDidChange
        this.isFieldHidden = configuration.isFieldHidden ?? NO
        this.fieldVisibilityDidChange = configuration.fieldVisibilityDidChange
        this.locked = configuration.locked
        this.enabled = YES
    }
}
